import React, { useMemo } from "react";
import PropTypes from "prop-types";
import { Pencil, Trash2 } from "lucide-react";
import { useTranslation } from "react-i18next";

// آیکن‌های مجاز
const ICON_MAP = {
  book: "📚",
  flask: "🧪",
  code: "💻",
  language: "🗣️",
  calculator: "🧮",
  globe: "🌍",
  art: "🎨",
  music: "🎵",
  history: "🏛️",
};

const CategoryCard = ({
  category,
  onEdit,
  onDelete,
  canEdit,                // حق ویرایش از نظر نقش/permission (org:categories:write)
  onClick,
  allowedCategoryIds,     // اختیاری: لیست آیدی‌هایی که کاربر به‌شون دسترسی دسته‌بندی داره
  showDisabledActions = false, // اگر true باشد، دکمه‌ها را به‌صورت غیرفعال نشان می‌دهیم
}) => {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language?.includes("fa");

  // عنوان و آیکن
  const title = category?.title || "-";
  const iconKey = category?.icon || "";
  const emoji = ICON_MAP[iconKey] || "📁";

  // اطلاعات کمّی
  const level = category?.level || 1;
  const examsCount = category?.stats?.examsCount || 0;
  const childrenCount = category?.stats?.childrenCount || 0;
  const plansCount = category?.stats?.plansCount || 0;
  const hasSubscription = !!category?.hasSubscription;

  // شمارنده
  const counters = useMemo(() => {
    const items = [];
    if (level === 3) {
      items.push(`${examsCount} ${t("category_card.exam_text")}`);
      if (hasSubscription) items.push(`${plansCount} ${t("category_card.plans_text")}`);
    } else {
      items.push(`${childrenCount} ${t("category_card.subcategory_text")}`);
    }
    return items.join(" • ");
  }, [level, examsCount, hasSubscription, plansCount, childrenCount, t]);

  // مجموعه‌ی دسترسی دسته‌بندی‌ها:
  const scopeSet = useMemo(() => {
    if (Array.isArray(allowedCategoryIds) && allowedCategoryIds.length) {
      return new Set(allowedCategoryIds.map(String));
    }
    const user = JSON.parse(localStorage.getItem("user") || "{}");
    return new Set((user?.categoryScopes || []).map(String));
  }, [allowedCategoryIds]);

  // فقط اگر کاربر حق ویرایش کلی دارد + آیتم داخل اسکوپ خود کاربر است
  const scopedCanEdit = canEdit && scopeSet.has(String(category?._id));

  // اگر بخواهی دکمه‌ها را نشان بدهی ولی غیرقابل کلیک:
  const actionVisible = scopedCanEdit || showDisabledActions;

  return (
    <div className="bg-white rounded-xl shadow-md p-5 border border-gray-200 hover:shadow-lg hover:border-purple-300 transition-all duration-200">
      {/* Header (کلیک برای ورود به کودکان) */}
      <div onClick={onClick} className="flex items-start gap-3 mb-3 cursor-pointer">
        <div className="rounded-lg bg-purple-50 text-purple-700 px-3 py-2 text-xl">
          <span aria-hidden="true">{emoji}</span>
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-bold text-gray-900 text-lg truncate">{title}</h3>
          <p className="text-sm text-gray-600 mt-1">
            {counters}
            {level ? <span className="text-gray-400"> • L{level}</span> : null}
          </p>
        </div>
      </div>

      {/* Actions */}
      {actionVisible && (
        <div
          className={`pt-3 border-t border-gray-100 flex ${
            isRtl ? "justify-between" : "justify-between flex-row-reverse"
          }`}
        >
          <div className="flex gap-2">
            <button
              onClick={scopedCanEdit ? () => onEdit?.(category) : undefined}
              className={`p-2 rounded-full transition-colors focus:outline-none 
                ${scopedCanEdit
                  ? "text-gray-500 hover:bg-gray-100 hover:text-indigo-600 focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                  : "text-gray-400 pointer-events-none opacity-40"
                }`}
              title={
                scopedCanEdit
                  ? t("category_card.edit_category")
                  : t("category_card.no_scope_for_edit")
              }
              aria-label={t("category_card.edit_category")}
              aria-disabled={!scopedCanEdit}
            >
              <Pencil size={18} />
            </button>

            <button
              onClick={scopedCanEdit ? () => onDelete?.(category?._id) : undefined}
              className={`p-2 rounded-full transition-colors focus:outline-none 
                ${scopedCanEdit
                  ? "text-red-500 hover:bg-red-100 hover:text-red-700 focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
                  : "text-gray-300 pointer-events-none opacity-40"
                }`}
              title={
                scopedCanEdit
                  ? t("category_card.delete_category")
                  : t("category_card.no_scope_for_delete")
              }
              aria-label={t("category_card.delete_category")}
              aria-disabled={!scopedCanEdit}
            >
              <Trash2 size={18} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

CategoryCard.propTypes = {
  category: PropTypes.shape({
    _id: PropTypes.string.isRequired,
    title: PropTypes.string,
    icon: PropTypes.string,
    level: PropTypes.number,
    hasSubscription: PropTypes.bool,
    stats: PropTypes.shape({
      examsCount: PropTypes.number,
      childrenCount: PropTypes.number,
      plansCount: PropTypes.number,
    }),
  }).isRequired,
  onEdit: PropTypes.func,
  onDelete: PropTypes.func,
  canEdit: PropTypes.bool,
  onClick: PropTypes.func,
  allowedCategoryIds: PropTypes.arrayOf(PropTypes.string), // اختیاری
  showDisabledActions: PropTypes.bool,                      // اختیاری
};

export default CategoryCard;
